import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon.jsx';
import { APP_CONFIG } from '../config/appConfig.js';

const { FiArrowRight, FiChevronDown, FiChevronUp, FiExternalLink } = FiIcons;

const NextSteps = () => {
  const [isOpen, setIsOpen] = useState(false);

  const steps = [
    {
      title: 'Compare with grid electricity',
      description: 'Compare your calculated cost per kWh with local grid electricity rates to quantify the "hidden tax" of unreliable power.'
    },
    {
      title: 'Calculate total monthly impact',
      description: 'Multiply your cost per kWh by your total monthly kWh consumption to understand the full financial impact.'
    },
    {
      title: 'Explore alternatives',
      description: 'Consider solar + battery systems, grid connection improvements, or more efficient generators as potential solutions.'
    },
    {
      title: 'Track and optimise',
      description: 'Monitor your actual fuel consumption and generator efficiency to identify optimisation opportunities.'
    }
  ];

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-8 py-6 text-left hover:bg-gray-50 transition-colors focus:outline-none focus:ring-4 focus:ring-violet-200"
            aria-expanded={isOpen}
            aria-controls="next-steps-content"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SafeIcon 
                  icon={FiArrowRight} 
                  className="text-2xl text-violet-600" 
                  aria-hidden="true"
                />
                <h2 className="text-2xl font-bold text-gray-900">
                  What to Do Next
                </h2>
              </div>
              <SafeIcon 
                icon={isOpen ? FiChevronUp : FiChevronDown} 
                className="text-xl text-gray-500" 
                aria-hidden="true"
              />
            </div>
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                id="next-steps-content"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="border-t border-gray-200"
              >
                <div className="px-8 py-6">
                  <div className="space-y-6">
                    {steps.map((step, index) => (
                      <motion.div
                        key={step.title}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="flex gap-4"
                      >
                        <div className="flex-shrink-0 w-8 h-8 bg-violet-100 text-violet-600 rounded-full flex items-center justify-center font-semibold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">
                            {step.title}
                          </h3>
                          <p className="text-gray-600">
                            {step.description}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Optional portal link */}
                  {APP_CONFIG.portalDebateUrl && (
                    <div className="mt-8 p-4 bg-violet-50 border border-violet-200 rounded-lg">
                      <h3 className="font-semibold text-violet-900 mb-2">
                        Join the Discussion
                      </h3>
                      <p className="text-sm text-violet-800 mb-3">
                        Share your results and learn from others facing similar power reliability challenges.
                      </p>
                      <a
                        href={APP_CONFIG.portalDebateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 font-medium text-sm transition-colors"
                      >
                        Visit Portal Debate Thread
                        <SafeIcon icon={FiExternalLink} className="text-sm" aria-hidden="true" />
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default NextSteps;